import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { useCreateStory, useStories } from '@/hooks/useStories';

import styles from './Stories.module.css';

export function Stories() {
  const { t } = useTranslation(['articles', 'common']);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'archived'>('active');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const { data: stories, isLoading } = useStories({
    status: status === 'all' ? undefined : status,
    search: search || undefined,
    limit: 100,
  });
  const createMutation = useCreateStory();

  async function handleCreate() {
    if (!newTitle.trim()) return;
    try {
      await createMutation.mutateAsync({
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
      });
      setNewTitle('');
      setNewDesc('');
      setShowCreate(false);
    } catch {
      // hook handles error state
    }
  }

  return (
    <section>
      <header className={styles.head}>
        <h1 className={styles.title}>{t('articles:stories.title')}</h1>
        <Badge tone="info" size="sm">
          Axioma I
        </Badge>
        {stories && (
          <span className={styles.totalBadge}>{stories.length} stories</span>
        )}
        <Button onClick={() => setShowCreate((s) => !s)} size="sm">
          {showCreate ? 'Cancelar' : '+ Nova story'}
        </Button>
      </header>

      <p className={styles.intro}>
        Cada story aglutina múltiplas manifestações: artigo WP, tweet, imagem
        DALL-E, post de horóscopo. Edite uma story → tudo o que pertence
        atualiza coerentemente.
      </p>

      {showCreate && (
        <div className={styles.createCard}>
          <input
            type="text"
            placeholder="Título da story…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className={styles.input}
          />
          <textarea
            placeholder="Descrição (opcional)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={2}
            className={styles.textarea}
          />
          <div className={styles.createActions}>
            <Button
              onClick={handleCreate}
              disabled={!newTitle.trim()}
              loading={createMutation.isPending}
            >
              Criar story
            </Button>
          </div>
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          type="search"
          placeholder="Buscar título…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />
        <div className={styles.statusFilters} role="group">
          {(['active', 'archived', 'all'] as const).map((s) => (
            <button
              key={s}
              className={`${styles.chip} ${status === s ? styles.chipActive : ''}`}
              onClick={() => setStatus(s)}
            >
              {s === 'all' ? 'Todas' : s === 'active' ? 'Ativas' : 'Arquivadas'}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className={styles.muted}>Carregando…</p>}

      {stories && stories.length === 0 && (
        <div className={styles.empty}>
          Nenhuma story ainda. Crie a primeira com o botão acima.
        </div>
      )}

      <ul className={styles.list}>
        {stories?.map((s) => (
          <li key={s.id} className={styles.row}>
            <div className={styles.body}>
              <Link to={`/stories/${s.id}`} className={styles.rowLink}>
                {s.title}
              </Link>
              {s.description && <p className={styles.desc}>{s.description}</p>}
              <div className={styles.meta}>
                <Badge
                  tone={s.status === 'active' ? 'ok' : 'neutral'}
                  size="xs"
                >
                  {s.status}
                </Badge>
                <span>
                  {s.manifestations_count} manifesta
                  {s.manifestations_count === 1 ? 'ção' : 'ções'}
                </span>
                <span>
                  atualizada{' '}
                  {formatarDataHora(s.updated_at, {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
